import React, {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	SortingState,
	getSortedRowModel,
	ColumnFiltersState,
	AccessorKeyColumnDef,
} from '@tanstack/react-table'
import { RefObject, useMemo, useState, useEffect } from 'react'
import ContactInfoList from '../../pages/Workspace/components/ContactInfoList'
import {
	Delete,
	FilterAltOff,
	InfoOutlined,
	EditOutlined,
	KeyboardArrowDown,
	KeyboardArrowUp,
	UnfoldMore,
	PushPin,
} from '@mui/icons-material'
import { useMediaQuery } from 'react-responsive'
import { ContactData } from '../../lib/api/types/contact.interfaces'
import { IconButton, OptionsMenuButton, Tooltip } from '../../components/ui'
import { ReactComponent as ResetColumnsSize } from '../../assets/icons/table/reset-columns.svg'
import cn from '../../lib/utils/cn'
import {
	ContactTableEntry,
	CustomColumnFiltersState,
} from '../../pages/Workspace/components/interfaces.table'
import { shortenFileName } from '../../lib/utils/formatDocument'
import FilterButton from '../../components/table/FilterButton'
import { useCustomGlobalFilter, useTextFilterContact } from './useColumnFilters'
import { useOutletContext } from 'react-router-dom'
import { MainLayoutType } from '../../components/layouts/MainLayout'

interface useCreateContactsTableProps {
	data?: ContactData[] | null
	visibleColumns: string[]
	selectedMobileColumn?: string
	containerRef?: RefObject<HTMLDivElement>
	searchQuery?: string
	onEditItem: (id: string) => void
	onFileInfo: (id: string) => void
	onDeleteItem: (id: string) => void
	isMobile?: boolean
}

const customGlobalFilter = useCustomGlobalFilter()
const textFilter = useTextFilterContact()

export const useCreateContactsTable = ({
	data = [],
	visibleColumns,
	selectedMobileColumn,
	containerRef,
	searchQuery,
	onFileInfo,
	onEditItem,
	onDeleteItem,
	isMobile,
}: useCreateContactsTableProps) => {
	const { isApp } = useOutletContext<MainLayoutType>()
	const [sorting, setSorting] = useState<SortingState>([])
	const [columnFilters, setColumnFilters] = useState<CustomColumnFiltersState>(
		[]
	)

	const renderSortingHeader = (
		title: string,
		column: React.Column<ContactTableEntry, unknown>,
		className?: string
	) => {
		return (
			<div
				className={cn(
					'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-4 box-border  dark:text-text-table-dark min-h-full',
					className
				)}
			>
				<FilterButton
					columnKey={column?.id}
					setColumnFilters={setColumnFilters}
					columnFilters={columnFilters}
					isDate={column?.id === 'deletedAt'}
					isSize={column?.id === 'size'}
					filterWindowClassName={cn(column?.id === 'fullName' && 'left-0')}
				/>
				<span className="text-center w-fit truncate">{title}</span>
				<span className=" flex items-center justify-center transition-opacity duration-200 w-5 h-5">
					{column.getIsSorted() === 'asc' && <KeyboardArrowUp />}
					{column.getIsSorted() === 'desc' && <KeyboardArrowDown />}
					{column.getIsSorted() === false && (
						<UnfoldMore className="h-5 opacity-0 group-hover:opacity-100 duration-default" />
					)}
				</span>
			</div>
		)
	}

	const tableData = useMemo(() => {
		const safeData = Array.isArray(data) ? data : []
		return safeData?.map((entry) => ({
			id: entry.id,
			fullName: entry.fullName,
			preferedName: entry.preferedName,
			company: entry.company,
			title: entry.title,
			emails: entry.emails.map((email) => ({
				type: email.type,
				value: email.value,
			})),
			phones: entry.phones.map((phone) => ({
				type: phone.type,
				value: phone.value,
			})),
			addressLine1: entry.addressLine1,
			addressLine2: entry.addressLine2,
			country: entry.country,
			state: entry.state,
			city: entry.city,
			zipCode: entry.zipCode,
			isPinned: entry.isPinned,
			controllers: {
				id: entry?.id,
			},
			options: {
				id: entry?.id,
			},
		}))
		// .sort((a, b) => {
		// 	if (b.title !== a.title) {
		// 		return b.title.localeCompare(a.title)
		// 	}

		// 	return b.fullName.localeCompare(a.fullName)
		// })
	}, [data])

	const isTablet = useMediaQuery({ minWidth: 769, maxWidth: 1024 })

	const fromLocationString = (str: string) => {
		if (typeof str !== 'string' || !str.trim()) return []
		const response = str
			.split(';')
			.filter((pair) => pair.includes(':') && pair.trim() !== '')
			.map((pair) => {
				const [type, ...rest] = pair.split(':')
				return { type, value: rest.join(':') }
			})
		return response
	}

	const columns = useMemo<ColumnDef<ContactTableEntry>[]>(() => {
		const base: ColumnDef<ContactTableEntry>[] = [
			{
				accessorKey: 'fullName',
				enableResizing: true,
				enableSorting: true,
				minSize: 80,
				size: isMobile ? 160 : 200,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => {
					return (
						<div className="h-full flex items-center gap-3 max-w-full pl-0 group cursor-pointer dark:text-stroke-dark">
							{renderSortingHeader('Full Name', column, 'w-fit pl-2')}
						</div>
					)
				},
				cell: ({ row }) => {
					const name = row?.original?.fullName ?? ''

					return (
						<div className="flex items-center gap-2 max-w-fit pl-0 overflow-ellipsis dark:text-text-main-dark">
							{row?.original?.isPinned && (
								<PushPin className="w-4 h-4 fill-text-secondary rotate-45" />
							)}
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'preferedName',
				meta: { label: 'Prefered name' },
				enableResizing: true,
				minSize: 80,
				size: isMobile ? 160 : 100,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Prefered Name', column),
				cell: ({ row }) => {
					const name = row?.original?.preferedName ?? ''
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-main-dark">
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'company',
				meta: { label: 'Company' },
				enableResizing: true,
				minSize: 80,
				size: isMobile ? 160 : 100,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Company', column),
				cell: ({ row }) => {
					const name = row?.original?.company ?? ''
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-main-dark">
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'title',
				meta: { label: 'Title' },
				enableResizing: true,
				minSize: 80,
				size: isMobile ? 160 : 100,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Title', column),
				cell: ({ row }) => {
					const name = row?.original?.title ?? ''
					return (
						<div className="flex items-center gap-2 max-w-fit md:pl-6 overflow-ellipsis dark:text-text-main-dark">
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'addressLine1',
				meta: { label: 'Address Line 1' },
				enableResizing: true,
				minSize: 80,
				size: isMobile ? 160 : 100,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Address Line 1', column),
				cell: ({ row }) => {
					const name = row?.original?.addressLine1 ?? ''
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-main-dark">
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'addressLine2',
				meta: { label: 'Address Line 2' },
				enableResizing: true,
				minSize: 80,
				size: isMobile ? 160 : 100,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Address Line 2', column),
				cell: ({ row }) => {
					const name = row?.original?.addressLine2 ?? ''
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-main-dark">
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'country',
				meta: { label: 'Country' },
				enableResizing: true,
				minSize: 80,
				size: isMobile ? 160 : 100,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Country', column),
				cell: ({ row }) => {
					const name = row?.original?.country ?? ''
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-main-dark">
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'state',
				meta: { label: 'State' },
				enableResizing: true,
				minSize: 80,
				size: isMobile ? 160 : 100,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('State', column),
				cell: ({ row }) => {
					const name = row?.original?.state ?? ''
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-main-dark">
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'city',
				meta: { label: 'City' },
				enableResizing: true,
				minSize: 80,
				size: isMobile ? 160 : 100,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('City', column),
				cell: ({ row }) => {
					const name = row?.original?.city ?? ''
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-main-dark">
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'zipCode',
				meta: { label: 'Zip Code' },
				enableResizing: true,
				minSize: 80,
				size: isMobile ? 160 : 100,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Zip Code', column),
				cell: ({ row }) => {
					const name = row?.original?.zipCode ?? ''
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-main-dark">
							<Tooltip
								label={name.toString()}
								className={cn(isMobile && 'opacity-0')}
							>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile
										? shortenFileName(name.toString())
										: name.toString()}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'emails',
				meta: { label: 'Email' },
				enableResizing: true,
				minSize: 80,
				size: isMobile ? 160 : 100,
				accessorFn: (row) => row.emails.map((e) => e.value).join(', '),
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Email', column),
				cell: ({ row }) => {
					const emails = row?.original?.emails ?? []

					const handleEmailClick = (email: string) => {
						if (email && email !== '-') {
							window.open(`mailto:${email}`, '_blank')
						}
					}

					// function formatEmail(email: string) {
					// 		const [localPart, domain] = email.split("@");
					// 		if (localPart.length > 8) {
					// 			return localPart.slice(0, 4) + "..." + "@" + domain;
					// 		}
					// 		return email;
					// }
					return (
						<div className="flex items-center max-w-fit overflow-ellipsis text-text-main truncate dark:text-text-table-dark w-full dark:bg-inherit">
							<p
								className={cn(
									'truncate cursor-pointer  transition-colors duration-200',
									!isApp && 'text-blue-400 hover:text-accent-hover'
								)}
								onClick={() => {
									!isApp && handleEmailClick(emails[0]?.value)
								}}
							>
								{emails[0]?.value || '-'}
							</p>
							<Tooltip
								label={'See other emails'}
								className={cn(isMobile && 'opacity-0')}
							>
								<span className="right-0 hover:bg-inherit dark:bg-inherit rounded-sm">
									{emails?.length > 1 && (
										<span className="text-xs text-text-secondary dark:text-text-secondary-dark dark:bg-inherit">
											<ContactInfoList
												contacts={emails}
												className=""
												onEmailClick={handleEmailClick}
											/>
										</span>
									)}
								</span>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'phones',
				meta: { label: 'Phone' },
				enableResizing: true,
				minSize: 80,
				size: isMobile ? 160 : 100,
				enableSorting: true,
				accessorFn: (row) => row.phones.map((p) => p.value).join(', '),
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Phone', column),
				cell: ({ row }) => {
					const names = row?.original?.phones ?? []
					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis dark:text-text-main-dark">
							<Tooltip
								label={'See other emails'}
								className={cn(isMobile && 'opacity-0')}
							>
								<div className="text-text-main flex items-center truncate dark:text-text-table-dark w-full z-50">
									{names[0]?.value}
									<span className=" right-16 hover:bg-inherit dark:bg-inherit rounded-sm">
										{names.length > 1 && (
											<span className="text-xs text-text-secondary dark:text-text-secondary-dark">
												<ContactInfoList contacts={names} className="" />
											</span>
										)}
									</span>
								</div>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'options',
				meta: { label: 'Options' },
				enableResizing: false,
				enableSorting: false,
				size: isMobile ? 100 : columnFilters?.length ? 50 : 30,
				maxSize: 50,
				minSize: 25,
				header: () => (
					<div className="flex items-center justify-end gap-1">
						<IconButton
							variant="transparent"
							className="w-8 h-8 group hover:bg-transparent"
							tooltipPosition="left"
							icon={
								<ResetColumnsSize className="fill-text-table w-5 h-5 group-hover:fill-accent-hover" />
							}
							onClick={() => table.resetColumnSizing()}
							tooltip={'Reset columns sizes'}
						/>
						{!!columnFilters?.length && (
							<IconButton
								variant="transparent"
								className="w-8 h-8 group hover:bg-transparent"
								tooltipPosition="left"
								icon={
									<FilterAltOff className="fill-text-table w-5 h-5 group-hover:fill-accent-hover" />
								}
								onClick={() => setColumnFilters([])}
								tooltip={'Clean filters'}
							/>
						)}
					</div>
				),
				cell: ({ row }) => {
					const { id, fullName } = {
						id: row.original.id,
						fullName: row?.original?.fullName,
					}
					return (
						<div className="bg-inherit md:absolute right-0 flex items-center justify-end gap-2 w-fit md:ml-auto pl-4 md:pr-2 h-full group-hover:lg:left-custom-shadow">
							{isMobile ? (
								<OptionsMenuButton
									buttonClassName="ml-auto mr-10"
									parentScrollRef={containerRef}
									items={[
										{
											icon: <InfoOutlined className="w-4" />,
											label: 'File info',
											onClick: () => onFileInfo(id),
										},
										{
											icon: <EditOutlined className="w-4" />,
											label: 'Edit',
											onClick: () => onEditItem(id),
										},
										{
											icon: <Delete className="w-4" />,
											label: 'Delete',
											onClick: () => {
												onDeleteItem(id)
											},
										},
									]}
								/>
							) : (
								<div className="flex items-center justify-center opacity-0 md:opacity-100 gap-2 lg:opacity-0 lg:group-hover:opacity-100 transition-all duration-150 ease-in-out">
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant="secondary"
										icon={
											<InfoOutlined className="w-4 dark:fill-text-main-dark" />
										}
										onClick={() => onFileInfo(id)}
										tooltip={'Contact Info'}
									/>
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant={'secondary'}
										icon={
											<EditOutlined className="w-4 dark:fill-text-main-dark" />
										}
										tooltip={'Edit'}
										onClick={() => onEditItem(id)}
									/>
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant="secondary"
										icon={<Delete className="w-4 dark:fill-text-main-dark" />}
										tooltip="Delete"
										onClick={() => {
											onDeleteItem(id)
										}}
									/>
								</div>
							)}
						</div>
					)
				},
			},
		]

		if (isMobile) {
			return base.filter(
				(
					columnDef
				): columnDef is AccessorKeyColumnDef<ContactTableEntry, any> => {
					const available = ['id', 'fullName', 'options']
					selectedMobileColumn && available?.push(selectedMobileColumn)
					return (
						'accessorKey' in columnDef &&
						available.includes(columnDef.accessorKey) &&
						columnDef.accessorKey !== 'controllers' &&
						columnDef.accessorKey !== 'type'
					)
				}
			)
		}

		if (isTablet) {
			return base.filter(
				(
					columnDef
				): columnDef is AccessorKeyColumnDef<ContactTableEntry, any> => {
					return (
						'accessorKey' in columnDef &&
						[
							'fullName',
							'emails',
							'phones',
							'city',
							'addressLine1',
							'company',
							'options',
						].includes(columnDef.accessorKey) &&
						columnDef.accessorKey !== 'controllers' &&
						columnDef.accessorKey !== 'type'
					)
				}
			)
		}

		return base.filter(
			(
				columnDef
			): columnDef is AccessorKeyColumnDef<ContactTableEntry, any> => {
				return (
					'accessorKey' in columnDef &&
					[
						'id',
						'fullName',
						'preferedName',
						'company',
						'title',
						'emails',
						'phones',
						'addressLine1',
						'addressLine2',
						'country',
						'state',
						'city',
						'zipCode',
						'options',
					].includes(columnDef.accessorKey) &&
					columnDef.accessorKey !== 'controllers' &&
					columnDef.accessorKey !== 'type'
				)
			}
		)
	}, [data, columnFilters, isMobile, selectedMobileColumn, isTablet])

	const table = useReactTable({
		data: tableData,
		columns,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		globalFilterFn: customGlobalFilter,
		manualSorting: false,
		enableSorting: true,
		enableSortingRemoval: true,
		onSortingChange: setSorting,
		onColumnFiltersChange: (filters) => {
			const newFilters: CustomColumnFiltersState = (
				filters as ColumnFiltersState
			).map((filter) => ({
				id: filter.id,
				type: (filter.value as any)?.type ?? 'contain',
				value:
					(filter.value as any)?.type === 'between'
						? {
							startValue: (filter.value as any)?.value?.startValue ?? null,
							endValue: (filter.value as any)?.value?.endValue ?? null,
						}
						: (filter.value as any)?.value ?? '',
			}))

			if (JSON.stringify(columnFilters) !== JSON.stringify(newFilters)) {
				setColumnFilters(newFilters)
			}
		},
		state: {
			sorting,
			columnFilters: useMemo(
				() =>
					columnFilters.map((filter) => ({
						id: filter.id,
						value: { type: filter.type, value: filter.value },
					})),
				[columnFilters]
			),
			globalFilter: searchQuery,
		},
	})

	useEffect(() => {
		if (isMobile) return
		table.getAllColumns().forEach((column) => {
			table
				.getColumn(column.id)
				?.toggleVisibility(visibleColumns.includes(column.id))
		})
	}, [visibleColumns, table])

	return { table }
}
